import { useEffect, useState } from "react";
import { Link, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { adminMe, adminLogout } from "../../lib/admin";
import { Loader2, LogOut, LayoutDashboard, Library, Users, FileText, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const NAV = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/admin/leads", label: "Leads", icon: Users },
    { to: "/admin/failed-reports", label: "Failed Reports", icon: AlertTriangle },
    { to: "/admin/library", label: "Wedding Intelligence", icon: Library },
];

export function AdminGuard() {
    const [status, setStatus] = useState("checking"); // checking | ok | denied
    const location = useLocation();

    useEffect(() => {
        let alive = true;
        adminMe()
            .then(() => { if (alive) setStatus("ok"); })
            .catch(() => { if (alive) setStatus("denied"); });
        return () => { alive = false; };
    }, []);

    if (status === "checking") {
        return (
            <div className="flex min-h-screen items-center justify-center bg-ivory">
                <Loader2 className="h-5 w-5 animate-spin text-gold" />
            </div>
        );
    }
    if (status === "denied") {
        return <Navigate to="/admin/login" state={{ from: location }} replace />;
    }
    return <AdminShell><Outlet /></AdminShell>;
}

function AdminShell({ children }) {
    const navigate = useNavigate();
    const location = useLocation();

    async function onLogout() {
        try {
            await adminLogout();
            toast.success("Signed out.");
        } catch (_) { /* noop */ }
        navigate("/admin/login", { replace: true });
    }

    return (
        <div className="min-h-screen bg-ivory">
            <header className="border-b border-charcoal/8 bg-white">
                <div className="mx-auto flex max-w-7xl items-center gap-6 px-6 py-4 md:px-10">
                    <Link to="/admin" className="font-serif text-lg text-charcoal" data-testid="admin-brand">
                        Vowze <span className="text-gold">·</span> <span className="text-charcoal/55">Admin</span>
                    </Link>
                    <nav className="ml-6 hidden md:flex md:items-center md:gap-1">
                        {NAV.map((n) => {
                            const active = n.end ? location.pathname === n.to : location.pathname.startsWith(n.to);
                            const Icon = n.icon;
                            return (
                                <Link
                                    key={n.to}
                                    to={n.to}
                                    data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    className={`inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-[12.5px] transition ${
                                        active
                                            ? "bg-charcoal text-ivory"
                                            : "text-charcoal/70 hover:text-charcoal"
                                    }`}
                                >
                                    <Icon className="h-3.5 w-3.5" />
                                    {n.label}
                                </Link>
                            );
                        })}
                    </nav>
                    <button
                        type="button"
                        onClick={onLogout}
                        data-testid="admin-logout"
                        className="ml-auto inline-flex items-center gap-2 rounded-full border border-charcoal/10 px-3.5 py-2 text-[12px] text-charcoal hover:border-charcoal"
                    >
                        <LogOut className="h-3.5 w-3.5" /> Sign out
                    </button>
                </div>
            </header>
            <main className="mx-auto max-w-7xl px-6 py-10 md:px-10">{children}</main>
        </div>
    );
}

export function AdminLogin() {
    const [pw, setPw] = useState("");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    async function submit(e) {
        e.preventDefault();
        setLoading(true);
        try {
            const { adminLogin } = await import("../../lib/admin");
            await adminLogin(pw);
            toast.success("Welcome back.");
            navigate("/admin", { replace: true });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Login failed.");
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="flex min-h-screen items-center justify-center bg-charcoal text-ivory">
            <form onSubmit={submit} className="w-full max-w-sm rounded-[28px] bg-ivory p-8 text-charcoal shadow-soft" data-testid="admin-login-card">
                <div className="font-serif text-2xl">Vowze <span className="text-gold">·</span> Admin</div>
                <p className="mt-2 text-[13px] text-ink">Sign in to manage analyses, leads, and the Wedding Intelligence Library.</p>
                <label className="mt-7 block text-[10px] uppercase tracking-[0.22em] text-charcoal/55">Password</label>
                <input
                    type="password"
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    autoFocus
                    data-testid="admin-login-password"
                    className="mt-2 w-full rounded-full border border-charcoal/15 bg-white px-4 py-3 text-[14px] text-charcoal focus:border-charcoal focus:outline-none"
                />
                <button
                    type="submit"
                    disabled={loading}
                    data-testid="admin-login-submit"
                    className="btn-gold mt-6 w-full"
                >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
                </button>
            </form>
        </div>
    );
}

export { FileText };
